import { iconNames, type IconName } from '../components/ui/Icon';

export type DesignStatus = 'Draft' | 'Review' | 'Approved';

export interface HandoffPage {
  id: string;
  title: string;
  href: string;
  description: string;
  states: string[];
}

export interface HandoffAsset {
  name: string;
  href: string;
  kind: 'iconfont' | 'sprite' | 'image';
  description: string;
  download: boolean;
}

export interface HandoffConfig {
  projectName: string;
  version: string;
  designStatus: DesignStatus;
  build: {
    label: string;
    date: string;
  };
  viewport: {
    baseline: { width: number; height: number };
    minimumWidth: number;
  };
  pages: HandoffPage[];
  interactions: string[];
  icons: IconName[];
  assets: HandoffAsset[];
}

export const handoffConfig: HandoffConfig = {
  projectName: 'WOne PC Design',
  version: '0.1.0',
  designStatus: 'Draft',
  build: {
    label: '设计交付项目初始化',
    date: '2026-08-21',
  },
  viewport: {
    baseline: { width: 1440, height: 900 },
    minimumWidth: 1280,
  },
  pages: [
    {
      id: 'student-management',
      title: '学员信息管理',
      href: '/modules/front-desk/学员信息管理',
      description: '前台业务学员信息列表，使用统一的 20 人本地模拟数据，与收费搜索共享稳定学员 ID；不连接真实接口、不持久化修改。签到、转班、合并等业务入口及沟通管理暂未启用。',
      states: ['查询/重置', '正式/非正式与跨校区筛选', '排序与分页', '仅当前页勾选', '自定义显示字段', '只读学员详情', '导出当前筛选结果（脱敏 CSV）'],
    },
    {
      id: 'charge',
      title: '收费',
      href: '/modules/front-desk/收费',
      description: '前台业务收费页面，通过右侧快速收费按钮打开二维码收款弹窗；快速收费不再作为独立导航入口。',
      states: ['初始选择收费学员', '关键词匹配学员（本地模拟）', '点击/键盘选中并回填', '无匹配学员', '快速收费弹窗', '静态二维码', '设置/清除演示金额', '生成并下载海报'],
    },
    {
      id: 'page-settings',
      title: '页面设置 · 收费海报自定义 v1',
      href: '/page-settings',
      description: '按 Figma 页面设置实现，五套主题、学校 Logo、自定义背景、背景色与文案即时预览。',
      states: ['默认预览', '主题切换', '图片读取', '上传格式/大小错误', '自定义背景', 'Logo 移除', '文案计数'],
    },
    {
      id: 'welcome',
      title: '欢迎页',
      href: '/',
      description: '用于确认项目、设计系统和交付链路已就绪。',
      states: ['默认'],
    },
    {
      id: 'admin',
      title: '管理后台 v2',
      href: '/admin',
      description: '后台入口、56px 顶栏、主菜单与二级菜单两列导航（复用 ModuleSidebar，分组默认收起、二级菜单常驻）与面包屑。',
      states: ['欢迎页', '分组展开/收起', '整栏展开/收起', '二级菜单', '菜单定位'],
    },
    {
      id: 'order-import-v2',
      title: '订单导入 2',
      href: '/admin/v2?page=data-import&view=order-import-v2',
      description: '基于原订单导入完整复制的独立新方案，上传状态、导入记录与草稿独立保存。',
      states: ['默认', '上传与校验', '在线处理', '导入记录'],
    },
    {
      id: 'projects',
      title: '项目页面',
      href: '/projects',
      description: '当前作为未来交付迭代项目的统一入口。',
      states: ['占位'],
    },
    {
      id: 'components',
      title: '组件预览',
      href: '/components',
      description: '统一查看当前公共组件、状态和公开调用方式。',
      states: ['总览'],
    },
    {
      id: 'teacher-schedule',
      title: '教室课表 v3',
      href: '/teacher-schedule',
      description: '按现有校管家课表和点名页面复刻：面包屑、页签、教室卡片、课程统计与时间线。',
      states: ['默认', '教务侧边栏展开/收起', '教室切换', '宫格/列表', '空课表', '点名演示'],
    },
    {
      id: 'coupon-management',
      title: '优惠券管理 v1',
      href: '/coupon-management',
      description: '迁移优惠券列表、编辑优惠券与叠加规则设置原型；页面内使用本地 fixtures 演示状态。',
      states: ['筛选', '启用/停用反馈', '编辑优惠券', '叠加规则设置', '新增/编辑规则', '优惠券选择器', '叠加规则示例', '叠加范围详情', '删除确认'],
    },
    {
      id: 'recommendation-card-management',
      title: '电子推荐卡管理 v1',
      href: '/recommendation-card-management',
      description: '复刻电子推荐卡列表与创建页：名称与状态的查询筛选、按状态派生的操作集（推广/授权/编辑）与启用/停用开关、叠加范围列（老生券／新生券）与就地详情弹窗、分页，以及创建页的活动设置／奖励设置表单、日期选择器与手机模拟效果（预览入口在模拟效果上方）。',
      states: ['默认列表', '名称筛选', '状态筛选', '启用/停用反馈', '叠加范围详情弹窗', '空结果', '分页', '创建电子推荐卡（活动设置）', '创建电子推荐卡（奖励设置）', '编辑电子推荐卡'],
    },
  ],
  interactions: [
    '收费页内快速收费：二维码使用用户提供的示例图片，页面未接入真实收款服务；金额与老师信息仅在当前页面内演示，刷新后重置，不写入流水。下载始终导出同一张示意二维码，金额和老师开关不会改变二维码内容。',
    '页面设置 v1：主题、Logo、背景色和文案仅在当前页面内预览，离开或刷新后重置；Logo 限 2 MB、背景限 5 MB，支持 JPG/JPEG/PNG；图片读取与解码失败可重新选择。二维码为静态演示，无真实收款能力；其他未提供设计的页签暂不可用。',
    '当前包含设计交付页、导航和教室课表演示；所有顶部菜单的二级项均可进入带对应侧栏的空白内容占位页，不连接真实业务接口。',
    '后续页面使用静态 fixtures 演示交互、加载、空、错误和禁用状态。',
    '页面宽度低于 1280px 时保持 PC 最小画布，不重新排版为移动端。',
    '默认亮色；右上角可手动切换暗黑模式，选择保存在当前浏览器。',
    '教室课表 v3：固定 2026-09-04 11:23:36 演示时间；点名、操作、刷新和非当前页签仅展示反馈，不写入业务数据。',
    '优惠券管理 v1：筛选、启用/停用、编辑优惠券和单券/组合叠加规则只操作页面内 fixtures，不连接真实业务接口；叠加关系不会自动传递，删除规则只移除该规则产生的关系。',
    '电子推荐卡管理 v1：名称与状态筛选需点「查询」生效；新增会真写入页面内数据并派生「老生券／新生券」两张奖励券（进入叠加宇宙），保存路径只有「返回／保存」——两张券的叠加范围改到列表「叠加范围」两列点进去配，那里带具体券 id；启用/停用只改人工标志，不移动活动窗口；推广、授权、活动图片与分享图标上传、模板预览尚未设计，点击只回显反馈。',
    'F12 可按“语义 Token → 角色色 → 物理色阶 → HEX”追踪颜色；页面不得直接调用 Palette 或 Role。',
  ],
  icons: [...iconNames],
  assets: [
    {
      name: '下载 Iconfont 原始包',
      href: './downloads/w1-design-5225620.zip',
      kind: 'iconfont',
      description: 'Iconfont 项目 w1-design，项目 ID 5225620。',
      download: true,
    },
    {
      name: '下载标准化 Symbol Sprite',
      href: './iconfont/w1-design.symbol.svg',
      kind: 'sprite',
      description: '已移除硬编码颜色，所有图标使用 currentColor。',
      download: true,
    },
  ],
};
